import React, { createContext, useContext, useState, useEffect } from 'react';
import { themes } from '../styles/designTokens';

export type ThemeName = 'pink' | 'purple' | 'blue';

interface UserProfile {
  name: string;
  avatar: string;
  bio: string;
}

interface ThemeContextType {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  themeColors: typeof themes.pink.colors;
  userProfile: UserProfile;
  updateProfile: (profile: Partial<UserProfile>) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeName>('pink');
  const [userProfile, setUserProfile] = useState({
    name: 'Девушка-мечта',
    avatar: '👩‍💼',
    bio: 'Планирую свою идеальную жизнь! ✨'
  });

  const themeColors = themes[theme].colors;

  // Apply theme to CSS variables
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(themeColors).forEach(([key, value]) => {
      root.style.setProperty(`--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, value);
    });
  }, [themeColors]);

  // Load theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('girl-planner-theme') as ThemeName;
    const savedProfile = localStorage.getItem('girl-planner-profile');
    
    if (savedTheme && themes[savedTheme]) {
      setTheme(savedTheme);
    }
    
    if (savedProfile) {
      try {
        setUserProfile(JSON.parse(savedProfile));
      } catch (error) {
        console.error('Error parsing saved profile:', error);
      }
    }
  }, []);

  // Save theme to localStorage
  useEffect(() => {
    localStorage.setItem('girl-planner-theme', theme);
  }, [theme]);

  const updateProfile = (profile: Partial<UserProfile>) => {
    const newProfile = { ...userProfile, ...profile };
    setUserProfile(newProfile);
    localStorage.setItem('girl-planner-profile', JSON.stringify(newProfile));
  };

  const value = {
    theme,
    setTheme,
    themeColors,
    userProfile,
    updateProfile
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
